/**
 * Optimize Contentful images using the Images API
 * https://www.contentful.com/developers/docs/references/images-api/
 * 
 * @param {string} url - The Contentful image URL
 * @param {object} options - Optimization options
 * @param {number} options.width - Target width in pixels
 * @param {number} options.height - Target height in pixels (optional)
 * @param {number} options.quality - Quality 1-100 (default: 80)
 * @param {string} options.format - Image format: webp, jpg, png (default: webp)
 * @param {string} options.fit - Resize behavior: pad, fill, scale, crop, thumb (default: fill)
 * @param {string} options.focus - Focus area for cropping: center, top, right, left, bottom, top_right, top_left, bottom_right, bottom_left, face, faces (default: center)
 * @returns {string} Optimized image URL
 */
export const optimizeContentfulImage = (url, options = {}) => {
  if (!url) return ''
  
  // Don't optimize if not a Contentful image
  if (!url.includes('images.ctfassets.net') && !url.includes('assets.ctfassets.net')) {
    return url
  }

  if (url.includes('.svg')) {
    return url
  }

  const {
    width,
    height,
    quality = 80,
    format = 'webp',
    fit = 'fill',
    focus = 'center',
  } = options

  const params = new URLSearchParams()

  if (width) params.append('w', width)
  if (height) params.append('h', height)
  params.append('q', quality)
  params.append('fm', format)
  if (fit) params.append('fit', fit)
  if (focus && fit === 'thumb') params.append('f', focus)

  return `${url}?${params.toString()}`
}

/**
 * Preset sizes for common use cases
 */
export const IMAGE_PRESETS = {
  // Icons and small images
  icon: { width: 100, quality: 90 },
  iconLarge: { width: 200, quality: 90 },
  
  // Cards
  cardSmall: { width: 300, quality: 80 },
  cardMedium: { width: 400, quality: 80 },
  cardLarge: { width: 600, quality: 80 },
  
  // Hero images
  heroMobile: { width: 768, quality: 85 },
  heroTablet: { width: 1024, quality: 85 },
  heroDesktop: { width: 1920, quality: 85 },
  
  // Content images
  contentSmall: { width: 400, quality: 80 },
  contentMedium: { width: 800, quality: 80 },
  contentLarge: { width: 1200, quality: 80 },
  
  // Thumbnails
  thumbnail: { width: 150, height: 150, quality: 80, fit: 'thumb', focus: 'face' },
  thumbnailLarge: { width: 300, height: 300, quality: 80, fit: 'thumb', focus: 'face' },
  
  // Clinic/location images
  clinicCard: { width: 400, height: 300, quality: 80, fit: 'fill' },
  clinicHero: { width: 1200, height: 600, quality: 85, fit: 'fill' },
  
  // Blog/care note images
  blogCard: { width: 400, height: 250, quality: 80, fit: 'fill' },
  blogHero: { width: 1200, height: 600, quality: 85, fit: 'fill' },
}

/**
 * Get optimized image URL using a preset
 * 
 * @param {string} url - The Contentful image URL
 * @param {string} presetName - Name of the preset from IMAGE_PRESETS
 * @returns {string} Optimized image URL
 */
export const getOptimizedImage = (url, presetName) => {
  const preset = IMAGE_PRESETS[presetName]
  if (!preset) {
    console.warn(`Image preset "${presetName}" not found. Using default optimization.`)
    return optimizeContentfulImage(url, { width: 800 })
  }
  return optimizeContentfulImage(url, preset)
}

/**
 * Generate srcset for responsive images
 * 
 * @param {string} url - The Contentful image URL
 * @param {array} widths - Array of widths for srcset (e.g., [400, 800, 1200])
 * @param {object} options - Additional optimization options
 * @returns {string} srcset string
 */
export const generateSrcSet = (url, widths = [400, 800, 1200, 1600], options = {}) => {
  if (!url) return ''
  
  return widths
    .map(width => {
      const optimizedUrl = optimizeContentfulImage(url, { ...options, width })
      return `${optimizedUrl} ${width}w`
    })
    .join(', ')
}

export default optimizeContentfulImage
